import { useState, useEffect, memo, useRef, useCallback } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { SortableTableHeader, useSortableData } from "../ui/sortable-table";
import { FilterableTableHeader } from "../ui/filterable-table-header";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Edit, Trash2, Phone, PhoneCall, EyeOff, Building2, UserCheck, Users } from "lucide-react";
import { format } from "date-fns";
import { VoiceCallModal } from "./VoiceCallModal.jsx";
import { useAuth } from "../../hooks/useAuth";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../../store/slices/leadStatusSlice";
import { toast } from "sonner";
import { getFullName } from "../../services/common/util.js";
import { fetchDesks } from "../../services/crmServices/deskService";
import { searchUsers, fetchAvailableUsers } from "../../services/crmServices/userService";
import { fetchDistinctValues } from "../../services/crmServices/leadService";
import { searchPositions } from "../../services/crmServices/positionService";

export const LeadsTable = memo(function LeadsTable({
    leads,
    onSelectLead,
    isLeadSelected,
    showBulkSelection = false,
    onEdit,
    onDelete,
    loading,
    initLeads,
    refreshCurrentPage, // New prop: refresh current page without resetting
    onLeadUpdate, // New prop: update lead in Redux without refresh
    onModalClose, // New prop: callback when modal closes (for deferred refresh)
    showStatusBadges = false, // New prop
    showDeskInfo = true, // New prop
    showAgentInfo = true, // New prop
    onCurrentPageLeadsChange, // Callback to notify parent of current page leads
    serverSidePagination = false, // New prop: if true, use server-side pagination
    pagination = null, // Pagination metadata from server
    currentPage: serverCurrentPage = 1, // Current page from parent (server-side)
    onPageChange, // Callback to change page (server-side)
    pageSize: serverPageSize = 10, // Page size from parent (server-side)
    onPageSizeChange, // Callback to change page size (server-side)
    currentFilters = {}, // Current filter values
    onFilterChange, // Callback when filters change
    useServerSort = false, // If true, rely on server-side sorting (for Leads page)
    serverSortConfig = null, // External sort config from parent (for server sort)
    onServerSortChange // Callback when a sortable header is clicked (for server sort)
}) {
    const { user } = useAuth();
    const [clientCurrentPage, setClientCurrentPage] = useState(1);
    const [clientPageSize, setClientPageSize] = useState(10);
    const [showCallModal, setShowCallModal] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [selectedLeadIndex, setSelectedLeadIndex] = useState(-1);
    const leadStatuses = useSelector(selectLeadStatuses);
    
    // Add refs to cache positions to avoid multiple API calls
    const agentPositionsRef = useRef(null);
    const floorManagerPositionsRef = useRef(null);
    const hasLoadedFilterOptionsRef = useRef(false); // Add this to prevent duplicate calls
    
    // Add refs to track if sources/affiliates have been loaded
    const sourcesLoadedRef = useRef(false);
    const affiliatesLoadedRef = useRef(false);
    
    // Filter options state
    const [filterOptions, setFilterOptions] = useState({
        desks: [],
        agents: [],
        floorManagers: [],
        sources: [],
        affiliates: [],
        statuses: [],
        ageOptions: [
            { label: "Fresh (0-2 weeks)", value: "fresh" },
            { label: "Warm (2-6 weeks)", value: "warm" },
            { label: "Old (6+ weeks)", value: "old" }
        ],
        callCountRanges: [
            { label: "0-5 Calls", value: "0-5" },
            { label: "5-10 Calls", value: "5-10" },
            { label: "10-25 Calls", value: "10-25" },
            { label: "25+ Calls", value: "25+" }
        ]
    });
    
    const isAdmin = user?.position?.role === "admin";
    const isGeneralManager = user?.position?.role === "generalManager";
    const isFloorManager = user?.position?.role === "floorManager";
    const isDeskManager = user?.position?.role === "deskManager";
    const canFilterByAgent = isAdmin || isGeneralManager || isFloorManager || isDeskManager;

    const {
        sortedData: localSortedData,
        sortConfig: localSortConfig,
        handleSort: handleLocalSort
    } = useSortableData(leads, "updatedAt", "desc");

    // Use server-side or client-side pagination
    const isServerSide = serverSidePagination && pagination;

    // When using server-side sorting (Leads page), rely on backend order
    const effectiveSortConfig = useServerSort ? serverSortConfig : localSortConfig;
    const handleSort = (key) => {
        if (useServerSort && onServerSortChange) {
            onServerSortChange(key);
        } else {
            handleLocalSort(key);
        }
    };

    const sortedData = useServerSort ? leads : localSortedData;
    const currentPage = isServerSide ? serverCurrentPage : clientCurrentPage;
    const pageSize = isServerSide ? serverPageSize : clientPageSize;
    const totalPages = isServerSide ? (pagination?.totalPages || 1) : Math.ceil(sortedData.length / pageSize);
    
    // Reset to page 1 when leads change significantly (e.g., after filtering) - only for client-side
    useEffect(() => {
        if (!isServerSide && currentPage > totalPages && totalPages > 0) {
            setClientCurrentPage(1);
        }
    }, [sortedData.length, totalPages, currentPage, isServerSide]);
    
    // For server-side: display all leads (already paginated by server)
    // For client-side: paginate locally
    const displayLeads = isServerSide ? sortedData : (() => {
        const startIndex = (currentPage - 1) * pageSize;
        return sortedData.slice(startIndex, startIndex + pageSize);
    })();

    // Notify parent component of current page leads whenever they change
    useEffect(() => {
        if (onCurrentPageLeadsChange) {
            onCurrentPageLeadsChange(displayLeads);
        }
    }, [displayLeads, onCurrentPageLeadsChange]);

    const handlePrevious = () => {
        if (isServerSide && onPageChange) {
            onPageChange(Math.max(currentPage - 1, 1));
        } else {
            setClientCurrentPage((prev) => Math.max(prev - 1, 1));
        }
    };

    const handleNext = () => {
        if (isServerSide && onPageChange) {
            onPageChange(Math.min(currentPage + 1, totalPages));
        } else {
            setClientCurrentPage((prev) => Math.min(prev + 1, totalPages));
        }
    };

    const handleCall = (lead) => {
        // Check if user has call provider configured
        if (
            !(user.commpeak && user.permissions.voip?.commpeak) &&
            !(user.voiso && user.permissions.voip?.voiso) &&
            !(user.coperato && user.permissions.voip?.coperato) &&
            !(user.squaretalk && user.permissions.voip?.squaretalk) &&
            !(user.bubblevoip && user.permissions.voip?.bubblevoip)
        ) {
            toast.error("No call provider configured. Please contact administrator to set up VoIP.");
            return;
        }

        // Find the index in displayLeads (the actual displayed/sorted data)
        const pageLeadIndex = displayLeads.findIndex(l => l._id === lead._id);
        
        // Calculate actual index considering pagination
        let actualIndex = pageLeadIndex;
        if (isServerSide && pagination) {
            // For server-side pagination: actualIndex = (currentPage - 1) * pageSize + pageLeadIndex
            actualIndex = (currentPage - 1) * pageSize + pageLeadIndex;
        }
        
        setSelectedLead(lead);
        setSelectedLeadIndex(actualIndex);
        setShowCallModal(true);
    };
    
    // Handle navigation between leads in call modal
    const handleLeadNavigate = (newLead, newIndex) => {
        setSelectedLeadIndex(newIndex);
        
        if (newLead) {
            // Lead is provided (same page navigation)
            setSelectedLead(newLead);
        } else {
            // Lead is null (cross-page navigation)
            // The lead will be updated once the new page loads via useEffect below
        }
    };
    
    // Auto-select lead when page changes and we have a pending index
    useEffect(() => {
        if (selectedLeadIndex >= 0 && !selectedLead && displayLeads.length > 0) {
            // Calculate which lead should be selected on this page
            const indexInPage = selectedLeadIndex % pageSize;
            if (indexInPage >= 0 && indexInPage < displayLeads.length) {
                setSelectedLead(displayLeads[indexInPage]);
            }
        }
    }, [displayLeads, selectedLeadIndex, selectedLead, pageSize]);
    
    // Handle lead updates - update selectedLead if it's the same lead being edited
    const handleLeadUpdateWrapper = useCallback((updatedLead) => {
        // Always call the parent's onLeadUpdate
        if (onLeadUpdate) {
            onLeadUpdate(updatedLead);
        }
        
        // If the updated lead is the currently selected lead, update it in local state
        // BUT delay the update to prevent modal from closing/reopening during save
        // This keeps the lead in the modal even if it's filtered out from the list
        if (selectedLead && updatedLead) {
            const selectedId = selectedLead._id?.toString ? selectedLead._id.toString() : selectedLead._id;
            const updatedId = updatedLead._id?.toString ? updatedLead._id.toString() : updatedLead._id;
            
            if (selectedId === updatedId) {
                // Delay the update to prevent modal re-render during save
                // Use requestAnimationFrame to defer until after current render cycle
                requestAnimationFrame(() => {
                    setSelectedLead(updatedLead);
                });
            }
        }
    }, [onLeadUpdate, selectedLead]);

    // Fetch filter options - MUST be before any early returns
    useEffect(() => {
        // Prevent duplicate calls
        if (hasLoadedFilterOptionsRef.current) {
            return;
        }

        const loadFilterOptions = async () => {
            try {
                // Mark as loading to prevent duplicate calls
                hasLoadedFilterOptionsRef.current = true;

                // STEP 1: Fetch all positions once in parallel
                const [desksResponse, agentPositionsResponse, floorManagerPositionsResponse] = await Promise.all([
                    fetchDesks(),
                    searchPositions({ role: "agent" }),
                    (isAdmin || isGeneralManager) ? searchPositions({ role: "floorManager" }) : Promise.resolve({ data: [] })
                ]);
                
                // Cache positions
                agentPositionsRef.current = agentPositionsResponse?.data || [];
                floorManagerPositionsRef.current = floorManagerPositionsResponse?.data || [];
                
                // Process desks
                if (desksResponse?.data?.length) {
                    let filteredDesks = desksResponse.data;
                    if (isDeskManager) {
                        filteredDesks = desksResponse.data.filter(desk => {
                            const managerId = desk.manager?._id || desk.manager;
                            return managerId?.toString() === user._id?.toString();
                        });
                    } else if (isGeneralManager || isFloorManager) {
                        filteredDesks = desksResponse.data.filter(desk => {
                            const floorManagerId = desk.floorManager?._id || desk.floorManager;
                            return floorManagerId?.toString() === user._id?.toString() && desk.floor === user.position.floor;
                        });
                    }
                    setFilterOptions(prev => ({
                        ...prev,
                        desks: filteredDesks.map(desk => ({ label: desk.name, value: desk._id }))
                    }));
                    
                    // Fetch agents if user can filter by agent (using cached positions)
                    if (canFilterByAgent && agentPositionsRef.current.length > 0) {
                        let agentsResponse;
                        if (isAdmin) {
                            // Admin sees all agents
                            agentsResponse = await searchUsers({ 
                                position: { $in: agentPositionsRef.current.map((pos) => pos._id) } 
                            });
                        } else if (isGeneralManager) {
                            // General manager sees agents from all desks under their floor managers and desk managers
                            // The backend /user/v1/available endpoint already handles GM logic, but returns all users
                            // So we need to filter to only agents
                            const allUsersResponse = await fetchAvailableUsers();
                            if (allUsersResponse?.data?.length && agentPositionsRef.current.length > 0) {
                                // Filter to only include users with agent positions
                                const agentPositionIds = agentPositionsRef.current.map((pos) => pos._id?.toString() || pos._id);
                                agentsResponse = {
                                    data: allUsersResponse.data.filter(user => {
                                        const userPositionId = user.position?._id?.toString() || user.position?._id || user.position;
                                        return agentPositionIds.includes(userPositionId?.toString());
                                    })
                                };
                            }
                        } else if (isFloorManager) {
                            const managedDeskIds = filteredDesks.map(desk => desk._id?._id || desk._id).filter(Boolean);
                            if (managedDeskIds.length > 0) {
                                agentsResponse = await searchUsers({ 
                                    position: { $in: agentPositionsRef.current.map((pos) => pos._id) },
                                    agentDesk: { $in: managedDeskIds }
                                });
                            }
                        } else if (isDeskManager) {
                            const managedDeskIds = filteredDesks.map(desk => desk._id?._id || desk._id).filter(Boolean);
                            if (managedDeskIds.length > 0) {
                                agentsResponse = await searchUsers({ 
                                    position: { $in: agentPositionsRef.current.map((pos) => pos._id) },
                                    agentDesk: { $in: managedDeskIds }
                                });
                            }
                        }
                        if (agentsResponse?.data?.length) {
                            setFilterOptions(prev => ({
                                ...prev,
                                agents: agentsResponse.data.map(agent => ({ 
                                    label: getFullName(agent), 
                                    value: agent._id 
                                }))
                            }));
                        }
                    }
                }
                
                // Fetch floor managers (admin and GM) using cached positions
                if ((isAdmin || isGeneralManager) && floorManagerPositionsRef.current.length > 0) {
                    const floorManagersResponse = await searchUsers({
                        position: { $in: floorManagerPositionsRef.current.map((pos) => pos._id) }
                    });
                    if (floorManagersResponse?.data?.length) {
                        setFilterOptions(prev => ({
                            ...prev,
                            floorManagers: floorManagersResponse.data.map(fm => ({ 
                                label: getFullName(fm), 
                                value: fm._id 
                            }))
                        }));
                    }
                }
                
                // REMOVED: Don't fetch sources and affiliates here - fetch on demand when filter opens
                
                // Set statuses from Redux
                if (leadStatuses.length > 0) {
                    setFilterOptions(prev => ({
                        ...prev,
                        statuses: leadStatuses
                    }));
                }
            } catch (error) {
                console.error("Error loading filter options:", error);
                // Reset flag on error so it can retry
                hasLoadedFilterOptionsRef.current = false;
            }
        };
        
        loadFilterOptions();
    }, [isAdmin, isGeneralManager, isFloorManager, isDeskManager, canFilterByAgent, user._id, user.position?.floor, leadStatuses]);

    // Handle filter dropdown open/close - fetch distinct values when needed
    const handleFilterOpenChange = async (filterKey, isOpen) => {
        // Only fetch when opening and if not already loaded
        if (!isOpen) return;

        if (filterKey === 'source' && !sourcesLoadedRef.current) {
            sourcesLoadedRef.current = true;
            try {
                const sourcesResponse = await fetchDistinctValues('source');
                if (sourcesResponse?.data) {
                    setFilterOptions(prev => ({
                        ...prev,
                        sources: sourcesResponse.data.map(source => ({ label: source, value: source }))
                    }));
                }
            } catch (error) {
                console.error("Error fetching sources:", error);
                sourcesLoadedRef.current = false; // Allow retry on error
            }
        } else if (filterKey === 'affiliate' && !affiliatesLoadedRef.current) {
            affiliatesLoadedRef.current = true;
            try {
                const affiliatesResponse = await fetchDistinctValues('affiliate');
                if (affiliatesResponse?.data) {
                    setFilterOptions(prev => ({
                        ...prev,
                        affiliates: affiliatesResponse.data.map(affiliate => ({ label: affiliate, value: affiliate }))
                    }));
                }
            } catch (error) {
                console.error("Error fetching affiliates:", error);
                affiliatesLoadedRef.current = false; // Allow retry on error
            }
        }
    };

    const getCallButtonColor = () => {
        // if (user.commpeak && user.voiso) {
        //     return "text-green-600 hover:text-green-700"; // Both configured
        // } else if (user.commpeak || user.voiso) {
        //     return "text-blue-600 hover:text-blue-700"; // One configured
        // } else {
        //     return "text-gray-400"; // None configured
        // }
        return "text-green-600 hover:text-green-700";
    };

    const getCallButtonTitle = () => {
        const providers = [];
        if (user.commpeak && user.permissions.voip?.commpeak) providers.push("CommPeak");
        if (user.voiso && user.permissions.voip?.voiso) providers.push("Voiso");
        if (user.coperato && user.permissions.voip?.coperato) providers.push("Coperato");
        if (user.squaretalk && user.permissions.voip?.squaretalk) providers.push("SquareTalk");
        if (user.bubblevoip && user.permissions.voip?.bubblevoip) providers.push("Bubble VoIP");

        if (providers.length > 1) {
            return `Call via ${providers.join(" or ")}`;
        } else if (providers.length === 1) {
            return `Call via ${providers[0]}`;
        } else {
            return "No call provider configured";
        }
    };

    if (loading) {
        return (
            <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading leads...</p>
            </div>
        );
    }

    if (leads.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-lg mb-2">😔</div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Sorry! No Result Found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
        );
    }

    const onCloseVoiceCallModal = () => {
        setShowCallModal(false);
        // Check if refresh is needed (when lead was filtered out during save)
        // Refresh will be handled by parent component via callback
        if (onModalClose) {
            onModalClose();
        }
    };

    const handleCallSuccess = () => {
        // Only refresh when call is actually completed
        // Use refreshCurrentPage to avoid resetting to page 1
        if (refreshCurrentPage) {
            refreshCurrentPage();
        } else if (initLeads) {
            // Fallback to initLeads if refreshCurrentPage is not available
            initLeads();
        }
    };

    const getStatusConfig = (statusValue) => {
        return leadStatuses.find((status) => status.value === statusValue);
    };

    // Fast client-side age calculation based on lead creation date
    // Categories: 1-14 days = Fresh, 14-28 days = Warm, 28+ days = Old
    
    // Handle filter change
    const handleFilterChange = (filterKey, values) => {
        if (onFilterChange) {
            let newFilters = {
                ...currentFilters
            };
            
            // Handle age filter - convert to date ranges
            if (filterKey === "age") {
                if (values.length > 0) {
                    // Use UTC dates to avoid timezone issues
                    const now = new Date();
                    const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
                    const todayEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
                    
                    const hasOld = values.includes("old");
                    const hasFresh = values.includes("fresh");
                    const hasWarm = values.includes("warm");
                    
                    // Store age values for UI state
                    newFilters.age = values;
                    
                    if (hasOld && (hasFresh || hasWarm)) {
                        // If "old" is selected with other ages, we need to use a wide range
                        // Since "old" has no upper bound, we'll set from to a year ago
                        // and to to today (to include fresh/warm)
                        const fromDate = new Date(todayStart);
                        fromDate.setUTCFullYear(fromDate.getUTCFullYear() - 1); // Go back a year
                        
                        newFilters.registeredFrom = fromDate.toISOString().split('T')[0];
                        // Include end of day for today
                        newFilters.registeredTo = todayEnd.toISOString().split('T')[0] + 'T23:59:59.999Z';
                    } else if (hasOld) {
                        // Only "old" selected - 43+ days ago (more than 6 weeks)
                        const fromDate = new Date(todayStart);
                        fromDate.setUTCFullYear(fromDate.getUTCFullYear() - 1);
                        const toDate = new Date(todayEnd);
                        toDate.setUTCDate(toDate.getUTCDate() - 43); // 43 days ago to exclude warm (42 days)
                        
                        newFilters.registeredFrom = fromDate.toISOString().split('T')[0];
                        newFilters.registeredTo = toDate.toISOString().split('T')[0];
                    } else if (hasFresh && hasWarm) {
                        // Both fresh and warm selected: 0-42 days (from 42 days ago to today)
                        const fromDate = new Date(todayStart);
                        fromDate.setUTCDate(fromDate.getUTCDate() - 42);
                        
                        newFilters.registeredFrom = fromDate.toISOString().split('T')[0];
                        // Include end of day for today
                        newFilters.registeredTo = todayEnd.toISOString().split('T')[0] + 'T23:59:59.999Z';
                    } else if (hasFresh) {
                        // Only fresh selected: 0-14 days (0 to 2 weeks, from 14 days ago to today, inclusive)
                        const fromDate = new Date(todayStart);
                        fromDate.setUTCDate(fromDate.getUTCDate() - 14);
                        
                        newFilters.registeredFrom = fromDate.toISOString().split('T')[0];
                        // Use todayEnd which includes 23:59:59.999 - but backend needs full ISO string for end of day
                        // For now, sending date string - backend should handle end of day
                        newFilters.registeredTo = todayEnd.toISOString().split('T')[0] + 'T23:59:59.999Z';
                    } else if (hasWarm) {
                        // Only warm selected: 15-42 days ago (2 weeks to 6 weeks)
                        // From: 42 days ago (inclusive), To: 15 days ago (inclusive, end of day)
                        // This excludes fresh (0-14 days) and old (43+ days)
                        const fromDate = new Date(todayStart);
                        fromDate.setUTCDate(fromDate.getUTCDate() - 42);
                        
                        const toDate = new Date(todayEnd);
                        toDate.setUTCDate(toDate.getUTCDate() - 15);
                        
                        newFilters.registeredFrom = fromDate.toISOString().split('T')[0];
                        // Include end of day to capture all leads from 15 days ago
                        newFilters.registeredTo = toDate.toISOString().split('T')[0] + 'T23:59:59.999Z';
                    }
                } else {
                    // Remove age filter
                    delete newFilters.age;
                    // Check if registeredFrom/registeredTo were set by age filter
                    // If age was the only thing setting these dates, remove them
                    // Note: This might conflict with manual date filters - user should use date filter panel for manual dates
                    if (currentFilters.age && !currentFilters.registeredFrom && !currentFilters.registeredTo) {
                        // Age filter was the only source of date filters, so remove them
                        delete newFilters.registeredFrom;
                        delete newFilters.registeredTo;
                    }
                }
            } else {
                // For other filters, set directly
                newFilters[filterKey] = values.length > 0 ? values : undefined;
            }
            
            // Remove undefined values
            Object.keys(newFilters).forEach(key => {
                if (newFilters[key] === undefined || (Array.isArray(newFilters[key]) && newFilters[key].length === 0)) {
                    delete newFilters[key];
                }
            });
            onFilterChange(newFilters);
        }
    };
    
    const getLeadAge = (createdAt) => {
        if (!createdAt) return { label: "-", color: "gray" };
        
        const now = new Date();
        const created = new Date(createdAt);
        const diffTime = Math.abs(now - created);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        // 1-14 days: Fresh
        if (diffDays >= 1 && diffDays <= 14) {
            return { label: "Fresh", color: "green" };
        }
        // 14-28 days: Warm (exclusive start, inclusive end to avoid overlap)
        else if (diffDays > 14 && diffDays <= 28) {
            return { label: "Warm", color: "yellow" };
        }
        // 28+ days: Old
        else if (diffDays > 28) {
            return { label: "Old", color: "red" };
        }
        // Less than 1 day (0 days) - also show as Fresh
        else {
            return { label: "Fresh", color: "green" };
        }
    };

    return (
        <div className="space-y-3 w-full">
            <div className="rounded-lg border border-gray-200 shadow-sm bg-white">
                <Table style={{ minWidth: '1400px', width: 'auto' }} className="caption-bottom text-sm">
                        <TableHeader>
                            <TableRow className="bg-gray-50">
                                {showBulkSelection && <TableHead className="w-12 py-2 text-xs font-semibold text-gray-700"></TableHead>}
                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Actions</TableHead>
                                <FilterableTableHeader
                                    filterKey="callCountRange"
                                    options={filterOptions.callCountRanges}
                                    selectedValues={currentFilters.callCountRange || []}
                                    onFilterChange={handleFilterChange}
                                    placeholder="Filter by calls"
                                    className="w-20 py-2 text-xs font-semibold text-gray-700"
                                >
                                    Calls
                                </FilterableTableHeader>
                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Name</TableHead>
                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Email</TableHead>
                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Phone</TableHead>
                                {(user.position?.role === "admin" || user.position?.role === "generalManager" || user.position?.role === "floorManager" || user.position?.role === "deskManager" || user.permissions?.gridLeadsColumns?.age) && (
                                    <FilterableTableHeader
                                        filterKey="age"
                                        options={filterOptions.ageOptions}
                                        selectedValues={currentFilters.age || []}
                                        onFilterChange={handleFilterChange}
                                        placeholder="Filter by age"
                                        className="py-2 text-xs font-semibold text-gray-700"
                                    >
                                        Age
                                    </FilterableTableHeader>
                                )}
                                <FilterableTableHeader
                                    filterKey="statuses"
                                    options={filterOptions.statuses}
                                    selectedValues={currentFilters.statuses || []}
                                    onFilterChange={handleFilterChange}
                                    placeholder="Filter by status"
                                    className="w-[150px] py-2 text-xs font-semibold text-gray-700"
                                >
                                    Status
                                </FilterableTableHeader>
                                {showDeskInfo && (
                                    <FilterableTableHeader
                                        filterKey="desk"
                                        options={filterOptions.desks}
                                        selectedValues={currentFilters.desk || []}
                                        onFilterChange={handleFilterChange}
                                        placeholder="Filter by desk"
                                        className="py-2 text-xs font-semibold text-gray-700"
                                    >
                                        Desk
                                    </FilterableTableHeader>
                                )}
                                {user.position?.role === "admin" && (
                                    <FilterableTableHeader
                                        filterKey="floorManager"
                                        options={filterOptions.floorManagers}
                                        selectedValues={currentFilters.floorManager || []}
                                        onFilterChange={handleFilterChange}
                                        placeholder="Filter by floor manager"
                                        className="py-2 text-xs font-semibold text-gray-700"
                                    >
                                        Floor Mgr
                                    </FilterableTableHeader>
                                )}
                                {showAgentInfo && (
                                    <FilterableTableHeader
                                        filterKey="agent"
                                        options={[
                                            { label: "Unassigned", value: "unassigned" },
                                            ...filterOptions.agents
                                        ]}
                                        selectedValues={currentFilters.agent || []}
                                        onFilterChange={handleFilterChange}
                                        placeholder="Filter by agent"
                                        className="py-2 text-xs font-semibold text-gray-700"
                                    >
                                        Agent
                                    </FilterableTableHeader>
                                )}
                                <FilterableTableHeader
                                    filterKey="source"
                                    options={filterOptions.sources}
                                    selectedValues={currentFilters.source || []}
                                    onFilterChange={handleFilterChange}
                                    onOpenChange={handleFilterOpenChange}
                                    placeholder="Filter by source"
                                    className="py-2 text-xs font-semibold text-gray-700"
                                >
                                    Source
                                </FilterableTableHeader>
                                <FilterableTableHeader
                                    filterKey="affiliate"
                                    options={filterOptions.affiliates}
                                    selectedValues={currentFilters.affiliate || []}
                                    onFilterChange={handleFilterChange}
                                    onOpenChange={handleFilterOpenChange}
                                    placeholder="Filter by affiliate"
                                    className="py-2 text-xs font-semibold text-gray-700"
                                >
                                    Affiliate
                                </FilterableTableHeader>
                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Country</TableHead>
                                {/* Non-sortable communication columns on Leads page */}
                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Last Comm</TableHead>
                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Staff</TableHead>
                                <TableHead className="py-2 text-xs font-semibold text-gray-700">Comment</TableHead>
                                {user.permissions.gridLeadsColumns.registered && (
                                    <SortableTableHeader
                                        sortKey="createdAt"
                                        sortConfig={effectiveSortConfig}
                                        onSort={handleSort}
                                    >
                                        Registered
                                    </SortableTableHeader>
                                )}
                                {user.permissions.gridLeadsColumns.modified && (
                                    <SortableTableHeader
                                        sortKey="updatedAt"
                                        sortConfig={effectiveSortConfig}
                                        onSort={handleSort}
                                    >
                                        Modified
                                    </SortableTableHeader>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                        {displayLeads.map((lead) => (
                            <TableRow key={lead._id}>
                                {showBulkSelection && (
                                    <TableCell className="py-2">
                                        <Checkbox
                                            checked={isLeadSelected(lead._id)}
                                            onCheckedChange={(checked) => onSelectLead(lead, checked)}
                                        />
                                    </TableCell>
                                )}
                                <TableCell className="py-2">
                                    <div className="flex space-x-1">
                                        {user.permissions.leads.update && (
                                            <Button size="sm" variant="outline" onClick={() => onEdit(lead)} className="h-7 w-7 p-0">
                                                <Edit className="h-3 w-3" />
                                            </Button>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleCall(lead)}
                                            className={`${getCallButtonColor()} h-7 w-7 p-0`}
                                            title={getCallButtonTitle()}
                                        >
                                            <PhoneCall className="h-3 w-3" />
                                        </Button>

                                        {user.permissions.leads.delete && (
                                            <Button size="sm" variant="outline" onClick={() => onDelete(lead._id)} className="h-7 w-7 p-0">
                                                <Trash2 className="h-3 w-3 text-red-500" />
                                            </Button>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className="text-center">
                                    {(() => {
                                        const count = lead.callCount || 0;
                                        let bgColor, textColor;
                                        
                                        if (count >= 0 && count <= 5) {
                                            bgColor = 'bg-green-100';
                                            textColor = 'text-green-700';
                                        } else if (count > 5 && count <= 10) {
                                            bgColor = 'bg-yellow-100';
                                            textColor = 'text-yellow-700';
                                        } else if (count > 10 && count <= 25) {
                                            bgColor = 'bg-orange-100';
                                            textColor = 'text-orange-700';
                                        } else {
                                            bgColor = 'bg-red-100';
                                            textColor = 'text-red-700';
                                        }
                                        
                                        return (
                                            <Badge className={`${bgColor} ${textColor} border-0 font-mono font-bold`}>
                                                {count}
                                            </Badge>
                                        );
                                    })()}
                                </TableCell>
                                <TableCell className="font-medium">
                                    {/* Name is now a clickable link to View Lead */}
                                    <div className="flex items-center gap-2">
                                        <span
                                            className=""
                                            // onClick={() => onView(lead)}
                                            title="View Lead"
                                        >
                                            {lead.firstName + (lead.lastName ? " " + lead.lastName : "")}
                                        </span>
                                        {lead.isLive && (
                                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 px-1.5 py-0 text-xs font-bold">
                                                L
                                            </Badge>
                                        )}
                                        {lead.hasBeenTransferred && (
                                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 px-1.5 py-0 text-xs font-bold">
                                                T
                                            </Badge>
                                        )}
                                        {user.position.role === "admin" && lead.originalDesk && lead.desk && 
                                         (lead.originalDesk._id || lead.originalDesk) !== (lead.desk._id || lead.desk) && (
                                            <Badge 
                                                variant="outline" 
                                                className="bg-purple-50 text-purple-700 border-purple-300 px-1.5 py-0 text-xs"
                                                title={`Originally from: ${lead.originalDesk?.name || 'Unknown Desk'}`}
                                            >
                                                From: {lead.originalDesk?.name || 'Unknown'}
                                            </Badge>
                                        )}
                                    </div>
                                </TableCell>

                                <TableCell className="max-w-48 truncate">
                                    {user.permissions.leads.viewEmail ? lead.email : <EyeOff className="h-4 w-4" />}
                                </TableCell>
                                <TableCell>
                                    {lead.phone ? (
                                        <>
                                            {user.permissions.leads.viewPhone ? (
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-sm">{lead.phone}</span>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleCall(lead)}
                                                        className={`p-1 h-6 w-6 ${getCallButtonColor()}`}
                                                        title={getCallButtonTitle()}
                                                        disabled={
                                                            !(user.commpeak && user.permissions.voip?.commpeak) &&
                                                            !(user.voiso && user.permissions.voip?.voiso) &&
                                                            !(user.coperato && user.permissions.voip?.coperato) &&
                                                            !(user.squaretalk && user.permissions.voip?.squaretalk) &&
                                                            !(user.bubblevoip && user.permissions.voip?.bubblevoip)
                                                        }
                                                    >
                                                        <Phone className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <EyeOff className="h-4 w-4" />
                                            )}
                                        </>
                                    ) : (
                                        "-"
                                    )}
                                </TableCell>
                                {(user.position?.role === "admin" || user.position?.role === "generalManager" || user.position?.role === "floorManager" || user.position?.role === "deskManager" || user.permissions?.gridLeadsColumns?.age) && (
                                    <TableCell>
                                        {(() => {
                                            const age = getLeadAge(lead.createdAt);
                                            return (
                                                <Badge 
                                                    variant="outline"
                                                    className={`${
                                                        age.color === "green" ? "bg-green-100 text-green-800 border-green-300" :
                                                        age.color === "yellow" ? "bg-yellow-100 text-yellow-800 border-yellow-300" :
                                                        age.color === "orange" ? "bg-orange-100 text-orange-800 border-orange-300" :
                                                        "bg-red-100 text-red-800 border-red-300"
                                                    }`}
                                                >
                                                    {age.label}
                                                </Badge>
                                            );
                                        })()}
                                    </TableCell>
                                )}
                                <TableCell>
                                    {showStatusBadges && getStatusConfig ? (
                                        <Badge
                                            style={{ backgroundColor: getStatusConfig(lead.status)?.color }}
                                            className="text-white"
                                        >
                                            {getStatusConfig(lead.status)?.label || lead.status}
                                        </Badge>
                                    ) : (
                                        <Badge variant="secondary">{lead.status}</Badge>
                                    )}
                                </TableCell>
                                {showDeskInfo && (
                                    <TableCell>
                                        <div className="flex items-center space-x-2">
                                            <Building2 className="h-4 w-4 text-gray-500" />
                                            <span>{lead.desk?.name || "-"}</span>
                                        </div>
                                    </TableCell>
                                )}
                                {user.position?.role === "admin" && (
                                    <TableCell>
                                        <div className="flex items-center space-x-2">
                                            <Users className="h-4 w-4 text-gray-500" />
                                            <span>
                                                {getFullName(lead.floorManager) ||
                                                    (lead.desk?.floorManager ? getFullName(lead.desk.floorManager) : "-")}
                                            </span>
                                        </div>
                                    </TableCell>
                                )}
                                {showAgentInfo && (
                                    <TableCell>
                                        <div className="flex items-center space-x-2">
                                            <UserCheck className="h-4 w-4 text-gray-500" />
                                            <span>{getFullName(lead.agent) || "Unassigned"}</span>
                                        </div>
                                    </TableCell>
                                )}
                                <TableCell>{lead.source}</TableCell>
                                <TableCell>{lead.affiliate}</TableCell>
                                <TableCell>{lead.country || "-"}</TableCell>
                                <TableCell>
                                    {lead.lastCommDate ? format(new Date(lead.lastCommDate), "MMM dd, yyyy") : "-"}
                                </TableCell>
                                <TableCell>
                                    {lead.lastCommStaff
                                        ? lead.lastCommStaff.firstName +
                                          (lead.lastCommStaff.lastName ? " " + lead.lastCommStaff.lastName : "")
                                        : "-"}
                                </TableCell>
                                <TableCell className="max-w-32 truncate">{lead.lastComment || "-"}</TableCell>
                                {user.permissions.gridLeadsColumns.registered && (
                                    <TableCell>{format(new Date(lead.createdAt), "MMM dd, yyyy")}</TableCell>
                                )}
                                {user.permissions.gridLeadsColumns.modified && (
                                    <TableCell>{format(new Date(lead.updatedAt), "MMM dd, yyyy")}</TableCell>
                                )}
                            </TableRow>
                        ))}
                        </TableBody>
                    </Table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Show</span>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            const newPageSize = Number(e.target.value);
                            if (isServerSide && onPageSizeChange) {
                                onPageSizeChange(newPageSize);
                                if (onPageChange) onPageChange(1);
                            } else {
                                setClientPageSize(newPageSize);
                                setClientCurrentPage(1);
                            }
                        }}
                        className="border rounded px-2 py-1 text-sm"
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                    </select>
                    <span className="text-sm text-gray-700">entries</span>
                </div>

                <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm" onClick={handlePrevious} disabled={currentPage === 1}>
                        Previous
                    </Button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                        {isServerSide && pagination && (
                            <span className="text-xs text-gray-500 ml-2">
                                ({pagination.totalCount.toLocaleString()} total)
                            </span>
                        )}
                    </span>
                    <Button variant="outline" size="sm" onClick={handleNext} disabled={currentPage === totalPages}>
                        Next
                    </Button>
                    <div className="flex items-center space-x-2 ml-4">
                        <span className="text-sm text-gray-700">Go to:</span>
                        <input
                            type="number"
                            min="1"
                            max={totalPages}
                            placeholder={currentPage.toString()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    const pageNum = parseInt(e.target.value);
                                    if (pageNum >= 1 && pageNum <= totalPages) {
                                        if (isServerSide && onPageChange) {
                                            onPageChange(pageNum);
                                        } else {
                                            setClientCurrentPage(pageNum);
                                        }
                                        e.target.value = '';
                                    }
                                }
                            }}
                            className="border rounded px-2 py-1 text-sm w-16 text-center"
                        />
                    </div>
                </div>
            </div>

            <VoiceCallModal 
                open={showCallModal} 
                onClose={() => onCloseVoiceCallModal()} 
                lead={selectedLead}
                onSuccess={handleCallSuccess}
                onLeadUpdate={handleLeadUpdateWrapper}
                filteredLeads={displayLeads}
                currentLeadIndex={selectedLeadIndex}
                onNavigate={handleLeadNavigate}
                totalLeads={pagination?.totalCount || leads.length}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={onPageChange}
            />
        </div>
    );
});
